/* =========================================================
 * GoogleTagManager（GTM）でのeコマース追跡のための関数をまとめる
========================================================= */
const GTM_ECOMMERCE_VALUES = {
  VOLUNTEER: 300,
  JOB: 1500,
  VOLUNTEER_CATEGORY_NAME: "ボランティア",
  JOB_CATEGORY_NAME: "求人",
};

/* ---------------------------------------------------------
 enableGtmEcommerceTracking
 # 説明
 GA4のeコマース追跡を有効化する。view_item_list、select_itemなどのイベントを
 dataLayerにpushして、Googleアナリティクスで募集記事の表示・クリック状況を追跡する。
 # 使い方
 1. トラッキングしたいリストのWrapperに、`#gtm`接頭辞で始まるIDセレクタを指定
 2. article.gtm_ecommerce_dataを使用して、HTMLdata属性にパラメーターを埋め込み
   ※ 速度考慮のため募集一覧をESで取得している場合は、ArticlesIndexに保存しているgtm_ecommerce_dataを使用する
 3. 1で設定したIDセレクタで、ページ読み込み時等に
    `gtm_ecommerce_item_list_rendering`などのカスタムイベントを発火させる。
--------------------------------------------------------- */
function enableGtmEcommerceTracking() {
  window.addEventListener("gtm_ecommerce_item_list_rendering", (e) => {
    const wrapperId = e.detail.wrapperSelector.split("#")[1];
    const { userType } = e.detail;
    const globalIndex = e.detail.globalIndex || false;
    const itemsPerPage = e.detail.itemsPerPage || 30;

    if (!wrapperId) {
      console.warn("GTM Ecommerce: wrapperId not provided");
      return;
    }

    trackViewItemList(wrapperId, userType, globalIndex, itemsPerPage);
  });

  window.addEventListener("gtm_ecommerce_item_rendering", (e) => {
    const wrapperId = e.detail.wrapperSelector.split("#")[1];
    const { userType } = e.detail;

    if (!wrapperId) {
      console.warn("GTM Ecommerce: wrapperId not provided");
      return;
    }

    trackViewItem(wrapperId, userType);
  });

  window.addEventListener("gtm_ecommerce_add_to_wishlist", (e) => {
    const { articleId } = e.detail;
    const { userType } = e.detail;

    trackAddToWishlist(articleId, userType);
  });

  window.addEventListener("gtm_ecommerce_begin_checkout", (e) => {
    const wrapperId = e.detail.wrapperSelector.split("#")[1];
    const { userType } = e.detail;

    trackEntryEvent("begin_checkout", wrapperId, userType);
  });

  window.addEventListener("gtm_ecommerce_purchase", (e) => {
    const wrapperId = e.detail.wrapperSelector.split("#")[1];
    const { userType } = e.detail;

    trackEntryEvent("purchase", wrapperId, userType);
  });
}

/* ---------------------------------------------------------
 * private
--------------------------------------------------------- */

function pageNum() {
  const urlParams = new URLSearchParams(location.search);
  return Number(urlParams.get("page") || "1");
}

function trackViewItemList(
  wrapperId,
  userType,
  useGlobalIndex = false,
  itemsPerPage = 30
) {
  const wrapper = document.getElementById(wrapperId);
  const page = pageNum();

  if (!wrapper) {
    console.warn(`GTM Ecommerce: wrapper not found: ${wrapperId}`);
    return;
  }

  const articleElements = getFilteredEcommerceElements(wrapper, wrapperId);

  if (articleElements.length === 0) {
    console.warn(`GTM Ecommerce: no ecommerce items found in ${wrapperId}`);
    return;
  }

  const items = articleElements
    .map((element, index) =>
      buildEcommerceItem(element, { index, page, useGlobalIndex, itemsPerPage })
    )
    .filter((item) => item !== null);

  if (items.length === 0) {
    console.warn(`GTM Ecommerce: no valid items found in ${wrapperId}`);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: "view_item_list",
    user_type: userType,
    ecommerce: {
      item_list_id: wrapperId,
      items,
    },
  });

  enableSelectItemTracking(articleElements, items, wrapperId, userType);
}

function buildEcommerceItem(
  element,
  {
    index = 0,
    page = 1,
    useGlobalIndex = false,
    itemsPerPage = 30,
    withOutIndex = false,
  } = {}
) {
  try {
    const { dataset } = element;

    if (!dataset.gtmEcommerceItemId || !dataset.gtmEcommerceItemName) {
      console.warn(
        "GTM Ecommerce: missing required fields (item_id, item_name)",
        element
      );
      return null;
    }

    // NOTE: ページを考慮した累計index計算（0スタート）
    const itemIndex = useGlobalIndex
      ? index + (page - 1) * itemsPerPage
      : index;

    const item = {
      item_id: dataset.gtmEcommerceItemId,
      item_name: dataset.gtmEcommerceItemName,
      item_category: dataset.gtmEcommerceItemCategory || "",
      item_category2: dataset.gtmEcommerceItemCategory2 || "",
      item_category3: dataset.gtmEcommerceItemCategory3 || "",
      item_brand: dataset.gtmEcommerceItemBrand || "",
      price: parseFloat(dataset.gtmEcommercePrice || "0"),
      currency: dataset.gtmEcommerceCurrency || "JPY",
      // カスタムパラメータ
      activity_fee: parseFloat(dataset.gtmEcommerceActivityFee || "0"),
      genre: dataset.gtmEcommerceGenre || "",
      target_positions: dataset.gtmEcommerceTargetPositions || "",
      working_form: dataset.gtmEcommerceWorkingForm || "",
      character: dataset.gtmEcommerceCharacter || "",
      work_location: dataset.gtmEcommerceWorkLocation || "",
      work_location_prefecture:
        dataset.gtmEcommerceWorkLocationPrefecture || "",
      is_online_available: dataset.gtmEcommerceIsOnlineAvailable || "",
      activity_style: dataset.gtmEcommerceActivityStyle || "",
      is_promotion: dataset.gtmEcommerceIsPromotion === "true",
    };

    if (!withOutIndex) {
      item.index = itemIndex;
    }

    return item;
  } catch (error) {
    console.error("GTM Ecommerce: error building item data", error, element);
    return null;
  }
}

function enableSelectItemTracking(articleElements, items, wrapperId, userType) {
  articleElements.forEach((element, index) => {
    element.addEventListener(
      "click",
      (_e) => {
        const item = items[index];
        if (item) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: "select_item",
            user_type: userType,
            ecommerce: {
              item_list_id: wrapperId,
              items: [item],
            },
          });
        }
      },
      { once: true }
    );
  });
}

// NOTE: リストの中で除外するリストを定義
const ECOMMERCE_FILTER_RULES = {
  gtmMainSearch: {
    excludeSelectors: [".recommendUl"],
  },
};

// NOTE: リストの中にリストがある場合に、重複してトラッキングされないようにルールを元に除外する
function getFilteredEcommerceElements(wrapper, wrapperId) {
  const allElements = wrapper.querySelectorAll("[data-gtm-ecommerce-item-id]");
  const elements = Array.from(allElements);

  const filterRule = ECOMMERCE_FILTER_RULES[wrapperId];
  if (!filterRule) {
    return elements;
  }

  return elements.filter((element) => {
    if (filterRule.excludeSelectors) {
      const shouldExclude = filterRule.excludeSelectors.some((selector) =>
        element.closest(selector)
      );
      if (shouldExclude) return false;
    }

    if (filterRule.includeOnlySelectors) {
      return filterRule.includeOnlySelectors.some((selector) =>
        element.closest(selector)
      );
    }

    return true;
  });
}

function trackViewItem(wrapperId, userType) {
  const wrapper = document.getElementById(wrapperId);
  if (!wrapper) {
    console.warn(`GTM Ecommerce: wrapper not found: ${wrapperId}`);
    return;
  }

  const item = buildEcommerceItem(wrapper, { withOutIndex: true });

  if (!item) {
    console.warn(`GTM Ecommerce: no valid item found in ${wrapperId}`);
    return;
  }

  const value =
    item.item_category === GTM_ECOMMERCE_VALUES.VOLUNTEER_CATEGORY_NAME
      ? GTM_ECOMMERCE_VALUES.VOLUNTEER
      : GTM_ECOMMERCE_VALUES.JOB;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: "view_item",
    user_type: userType,
    ecommerce: {
      currency: "JPY",
      value,
      items: [item],
    },
  });
}

function trackAddToWishlist(articleId, userType) {
  const article = document.querySelector(
    `[data-gtm-ecommerce-item-id="${articleId}"]`
  );
  if (!article) {
    console.warn(`GTM Ecommerce: article not found: ${articleId}`);
    return;
  }

  const item = buildEcommerceItem(article, { withOutIndex: true });

  if (!item) {
    console.warn(
      `GTM Ecommerce: no valid item found for article: ${articleId}`
    );
    return;
  }

  const value =
    item.item_category === GTM_ECOMMERCE_VALUES.VOLUNTEER_CATEGORY_NAME
      ? GTM_ECOMMERCE_VALUES.VOLUNTEER
      : GTM_ECOMMERCE_VALUES.JOB;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: "add_to_wishlist",
    user_type: userType,
    ecommerce: {
      currency: "JPY",
      value,
      items: [item],
    },
  });
}

function trackEntryEvent(eventType, wrapperId, userType) {
  const wrapper = document.querySelector(`#${wrapperId}`);
  if (!wrapper) {
    return;
  }

  const item = buildEcommerceItem(wrapper, {
    itemsPerPage: 1,
    withOutIndex: true,
  });

  if (!item) {
    console.warn(`GTM Ecommerce: no valid item found in ${wrapperId}`);
    return;
  }

  const value =
    item.item_category === GTM_ECOMMERCE_VALUES.VOLUNTEER_CATEGORY_NAME
      ? GTM_ECOMMERCE_VALUES.VOLUNTEER
      : GTM_ECOMMERCE_VALUES.JOB;

  const transactionId = String(wrapper.dataset.gtmEcommerceTransactionId);

  const eventData = {
    event: eventType,
    user_type: userType,
    ecommerce: {
      currency: "JPY",
      value,
      items: [item],
    },
  };

  if (transactionId) {
    eventData.ecommerce.transaction_id = transactionId;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(eventData);
}

// NOTE: esbuild はモジュールレベルの副作用がない純粋な関数宣言をバンドルから除外する
// Sprockets からの移行のため、グローバルアクセスを維持するために window に公開する

export {
  enableGtmEcommerceTracking,
  pageNum,
  trackViewItemList,
  buildEcommerceItem,
  enableSelectItemTracking,
  getFilteredEcommerceElements,
  trackViewItem,
  trackAddToWishlist,
  trackEntryEvent,
};
